import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const API_BASE_URL = getApiBaseUrl();

const PaymentSuccess: React.FC = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [message, setMessage] = useState('');

  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const sessionId = query.get('session_id'); // Stripe
    const referenceId = query.get('reference'); // Paystack / PayPal
    const paymentRequestId = query.get('payment_request_id');
    const provider = query.get('provider');

    if (!paymentRequestId || !provider) {
      setStatus('error');
      setMessage('Missing required payment data in the URL.');
      return;
    }

    const verifyPayment = async () => {
      const token = localStorage.getItem('accessToken');
      const sessionId = searchParams.get('session_id'); // Stripe
      const referenceId =
        searchParams.get('paymentId') || searchParams.get('reference'); // PayPal & Paystack
      const paymentRequestId = searchParams.get('payment_request_id');
      const provider = searchParams.get('provider');

      if (!paymentRequestId || !provider) {
        setStatus('error');
        setMessage('Missing required payment data in the URL.');
        return;
      }

      try {
        let response;

        if (provider === 'stripe' && sessionId) {
          response = await fetch(
            `${API_BASE_URL}/payments/stripe/verify`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ sessionId, paymentRequestId }),
            }
          );
        } else if (provider === 'paypal' && referenceId) {
          response = await fetch(
            `${API_BASE_URL}/payments/paypal/verify`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ referenceId, paymentRequestId }),
            }
          );
        } else if (provider === 'paystack' && referenceId) {
          response = await fetch(
            `${API_BASE_URL}/payments/paystack/verify`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ referenceId, paymentRequestId }),
            }
          );
        } else {
          setStatus('error');
          setMessage('Missing or invalid payment data.');
          return;
        }

        if (!response.ok) {
          const text = await response.text();
          setStatus('error');
          setMessage(`Server returned error: ${text}`);
          return;
        }

        const data = await response.json();
        if (data.success) {
          setStatus('success');
          setMessage(
            data.description || 'Your payment was processed successfully.'
          );
        } else {
          setStatus('error');
          setMessage('Payment verification failed.');
        }
      } catch (error: any) {
        setStatus('error');
        setMessage(
          error.message || 'An error occurred while verifying payment.'
        );
      }
    };

    verifyPayment();
  }, [location.search]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-4">
      {status === 'loading' && (
        <>
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-6"></div>
          <h2
            className={`text-xl font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            Verifying Payment...
          </h2>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mt-2`}
          >
            Please wait while we confirm your transaction.
          </p>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircle className="w-14 h-14 text-green-500 mb-4" />
          <h2
            className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            Payment Successful
          </h2>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mt-2`}
          >
            {message}
          </p>
          <Link
            to="/dashboard/payments"
            className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Payments
          </Link>
        </>
      )}

      {status === 'error' && (
        <>
          <AlertCircle className="w-14 h-14 text-red-500 mb-4" />
          <h2
            className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            Payment Failed
          </h2>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mt-2`}
          >
            {message}
          </p>
          <Link
            to="/dashboard/payments"
            className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Payments
          </Link>
        </>
      )}
    </div>
  );
};

export default PaymentSuccess;
